// Weak ownership follows the body lifetime, with no roster/global retention.
const watched = new WeakMap();
const VISUAL_KEYS = ['driver', 'rig', 'blueprint', 'parts', 'hullMeshes', 'hullOutline', 'skinned', 'crownY', 'characterId', 'colorHex'];

/** Capture original visual children BEFORE indicators are attached. A body that
 * has begun a one-shot keeps its rig until an actual dead→alive edge. */
export function watchVisual(root) {
  if (!root || root.userData.skinned || watched.has(root)) return root;
  const driver = root.userData.driver;
  const state = { children: [...root.children], unsafe: !!driver?.dead, pose: null };
  watched.set(root, state);
  for (const method of ['cast', 'dash', 'hit', 'die']) {
    const original = driver?.[method];
    if (original) driver[method] = function (...args) { state.unsafe = true; return original.apply(this, args); };
  }
  const revive = driver?.revive;
  if (revive) driver.revive = function (...args) {
    const wasDead = this.dead;
    const result = revive.apply(this, args);
    if (wasDead && !this.dead) state.unsafe = false;
    return result;
  };
  const update = driver?.update;
  if (update) driver.update = function (now, dt, pose) {
    state.pose = [now, 0, { ...pose }];
    return update.call(this, now, dt, pose);
  };
  return root;
}

export function upgradeVisual(root, create, { before = () => {}, dispose = () => {}, after = () => {} } = {}) {
  const state = watched.get(root);
  if (!state || state.unsafe || root.userData.driver?.dead || root.userData.skinned) return false;
  const next = create();
  if (!next?.userData.skinned) return false;
  before(root);
  root.userData.driver?.dispose?.();
  for (const child of state.children) {
    root.remove(child);
    dispose(child);
  }
  for (const key of VISUAL_KEYS) delete root.userData[key];
  for (const key of VISUAL_KEYS) if (key in next.userData) root.userData[key] = next.userData[key];
  for (const child of [...next.children]) root.add(child);
  if (state.pose) root.userData.driver?.update?.(...state.pose);
  watched.delete(root);
  after(root);
  return true;
}
